"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { CookieConsent } from "@/lib/cookie-consent";
import { readConsent, writeConsent, clearConsent as clearConsentCookie, getDefaultConsent } from "@/lib/cookie-consent";

type CookieConsentContextValue = {
  consent: CookieConsent | null;
  setConsent: (value: CookieConsent) => void;
  /** Clear stored consent so the banner shows again. */
  clearConsent: () => void;
  /** True once we have read the cookie (client); consent may still be null if user has not chosen. */
  ready: boolean;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [consent, setConsentState] = useState<CookieConsent | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setConsentState(readConsent());
    setReady(true);
  }, []);

  const setConsent = useCallback((value: CookieConsent) => {
    writeConsent(value);
    setConsentState(value);
  }, []);

  const clearConsent = useCallback(() => {
    clearConsentCookie();
    setConsentState(null);
  }, []);

  return (
    <CookieConsentContext.Provider value={{ consent, setConsent, clearConsent, ready }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    return {
      consent: null,
      setConsent: () => {},
      clearConsent: () => {},
      ready: false,
    };
  }
  return ctx;
}

export { getDefaultConsent };
